/**
 * L'HISTORIQUE DES MAINTENANCES, côté écran — module PUR (28/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ MIROIR de `backend/src/historique-maintenance.ts` pour les FORMES. Le
 * serveur rassemble les deux sources (actes du plan, interventions de la fiche
 * de vie) et ne rend que du fait ; ce fichier ne sait que FILTRER ce qu'il a
 * reçu. Il est pur pour être testé sans écran (`front-historique-maintenance`).
 *
 * ── Deux choses que ce filtre refuse de faire ──────────────────────────────
 *
 *  · Il ne fait pas disparaître une ligne SANS DATE en silence. Choisir une
 *    période les écarte forcément (on ne sait pas où les ranger) — mais
 *    `filtrer` les COMPTE, et l'écran dit « 2 lignes sans date masquées par la
 *    période ». Un historique qui se croit complet est le pire des historiques.
 *  · Il ne compte pas les chips d'après un filtre qui les contredit : le compte
 *    d'un type se calcule avec TOUS les autres critères, sauf le type lui-même
 *    (sinon « Curatives 0 » dès qu'on a cliqué « Préventives »).
 */

import { libelleTypeDocument } from './documents-maintenance';

export type TypeMaintenance = 'preventive' | 'curative' | 'non_qualifiee';
/** `metrologie` : vérification métrologique, servie avec l'historique d'UN appareil (spec 002, récit 1). */
export type OrigineMaintenance = 'plan' | 'fiche' | 'metrologie';

export interface FichierHistorique {
  cle: string; name: string; ext: string; size: number;
  ajouteLe: string; ajoutePar: string;
  typeDoc: string;
  retireLe: string; retireParNom: string; motifRetrait: string;
}
/** `motif` : pourquoi une opération n'a pas été faite (exigé depuis le 28/10/2026). */
export interface OperationHistorique { operation: string; fait: boolean; motif?: string }

export interface EntreeHistorique {
  cle: string;
  origine: OrigineMaintenance;
  date: string;
  dateBrute: string;
  equipementCode: string;
  equipementNom: string;
  categorie: string;
  lieu: string;
  type: TypeMaintenance;
  natureDeduite: boolean;
  intitule: string;
  commentaire: string;
  intervenant: string;
  realisePar: 'interne' | 'prestataire' | 'inconnu';
  reference: string;
  numFacture: string;
  probleme: boolean;
  ncRef: string;
  conforme: boolean | null;
  reprise: boolean;
  dureeArret: string;
  piecesRechange: string;
  nbFichiers: number;
  prestataire: string;
  operateur: string;
  saisiPar: string;
  saisieLe: string;
  motifReprise: string;
  datePrevue: string;
  operations: OperationHistorique[];
  fichiers: FichierHistorique[];
  ivId: string;
  intervType: string;
  remiseLe: string;
  remisePar: string;
  remiseVerif: string;
  acteId: string;
  planFrequence: string;
  planOperations: string[];
  ponctualite: string;
  ponctualiteMotif: string;
  /** Intervention : 'sur_site', 'telemaintenance', ou '' (non précisé). */
  modalite: string;
}

export interface ResumeHistorique {
  total: number;
  preventives: number;
  curatives: number;
  nonQualifiees: number;
  curativesDeduites: number;
  avecProbleme: number;
  sansDate: number;
  repris: number;
}

export interface ReponseHistorique {
  aujourdhui: string;
  entrees: EntreeHistorique[];
  resume: ResumeHistorique;
  equipements: { code: string; nom: string }[];
  tronque: boolean;
}

export type Periode = 'tout' | '30j' | '90j' | '12m' | 'annee' | 'perso';

export const PERIODES: { k: Periode; label: string }[] = [
  { k: 'tout', label: 'Tout l’historique' },
  { k: '30j', label: '30 derniers jours' },
  { k: '90j', label: '90 derniers jours' },
  { k: '12m', label: '12 derniers mois' },
  { k: 'annee', label: 'Année en cours' },
  { k: 'perso', label: 'Dates précises…' },
];

export interface Criteres {
  q: string;
  /** '' = tous les types. */
  type: '' | TypeMaintenance;
  periode: Periode;
  /** Bornes ISO, lues seulement quand `periode === 'perso'`. */
  du: string;
  au: string;
  equipement: string;
  categorie: string;
  realisePar: '' | 'interne' | 'prestataire';
  origine: '' | OrigineMaintenance;
  /** Anomalie à l'acquittement, intervention non conforme, ou NC ouverte. */
  problemes: boolean;
  /** L'appareil a été arrêté (durée d'arrêt renseignée). */
  avecArret: boolean;
  /**
   * Les documents joints : '' = peu importe ; 'aucun' = aucun document ;
   * 'nonprecise' = au moins un document sans type ; sinon une clé de type
   * (`rapport`, `devis`, `facture`…) = au moins un document de CE type.
   */
  document: string;
  /** Écarte les actes retranscrits après coup (reprises). */
  sansReprises: boolean;
}

export const CRITERES_VIDES: Criteres = {
  q: '', type: '', periode: 'tout', du: '', au: '', equipement: '', categorie: '', realisePar: '',
  origine: '', problemes: false, avecArret: false, document: '', sansReprises: false,
};

const sansAccent = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Retranche `n` jours d'une date ISO (UTC : pas de décalage d'heure d'été). */
function moinsJours(iso: string, n: number): string {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, j - n)).toISOString().slice(0, 10);
}
function moinsMois(iso: string, n: number): string {
  const [a, m, j] = iso.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 - n, 1));
  // Le dernier jour du mois cible, si le jour d'origine n'y existe pas (31 → 30, 29/02).
  const dernier = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  d.setUTCDate(Math.min(j, dernier));
  return d.toISOString().slice(0, 10);
}

/** Les bornes [du, au] d'une période, '' = pas de borne. */
export function bornes(c: Pick<Criteres, 'periode' | 'du' | 'au'>, aujourdhui: string): { du: string; au: string } {
  switch (c.periode) {
    case '30j': return { du: moinsJours(aujourdhui, 30), au: '' };
    case '90j': return { du: moinsJours(aujourdhui, 90), au: '' };
    case '12m': return { du: moinsMois(aujourdhui, 12), au: '' };
    case 'annee': return { du: `${aujourdhui.slice(0, 4)}-01-01`, au: '' };
    case 'perso': return { du: c.du, au: c.au };
    default: return { du: '', au: '' };
  }
}

const periodeActive = (c: Criteres) => c.periode !== 'tout' && (c.periode !== 'perso' || !!c.du || !!c.au);

/** Texte cherchable d'une ligne : tout ce que l'écran en montre. */
function foin(e: EntreeHistorique): string {
  return sansAccent([
    e.equipementCode, e.equipementNom, e.intitule, e.commentaire, e.intervenant, e.reference,
    e.numFacture, e.piecesRechange, e.lieu, e.ncRef,
  ].join(' '));
}

export interface ResultatFiltre {
  lignes: EntreeHistorique[];
  /** Lignes sans date écartées PAR LA PÉRIODE — à dire à l'écran. */
  sansDateMasquees: number;
}

/**
 * Applique les critères. `sauf: 'type'` ignore le critère de type : c'est ce qui
 * sert à compter les chips (voir l'en-tête).
 */
export function filtrer(
  entrees: EntreeHistorique[], c: Criteres, aujourdhui: string, sauf?: 'type',
): ResultatFiltre {
  const { du, au } = bornes(c, aujourdhui);
  const parPeriode = periodeActive(c) && (!!du || !!au);
  const mots = sansAccent(c.q.trim()).split(/\s+/).filter(Boolean);
  let sansDateMasquees = 0;
  const lignes = entrees.filter((e) => {
    if (sauf !== 'type' && c.type && e.type !== c.type) return false;
    if (c.equipement && e.equipementCode !== c.equipement) return false;
    if (c.categorie && e.categorie !== c.categorie) return false;
    if (c.realisePar && e.realisePar !== c.realisePar) return false;
    if (c.origine && e.origine !== c.origine) return false;
    if (c.problemes && !(e.probleme || (e.ncRef && e.origine === 'plan'))) return false;
    if (c.avecArret && !e.dureeArret) return false;
    if (c.document) {
      const vivants = e.fichiers.filter((f) => !f.retireLe);
      if (c.document === 'aucun' ? vivants.length > 0
        : c.document === 'nonprecise' ? !vivants.some((f) => !f.typeDoc)
          : !vivants.some((f) => f.typeDoc === c.document)) return false;
    }
    if (c.sansReprises && e.reprise) return false;
    if (mots.length) {
      const f = foin(e);
      if (!mots.every((m) => f.includes(m))) return false;
    }
    if (parPeriode) {
      if (!e.date) { sansDateMasquees += 1; return false; }
      if (du && e.date < du) return false;
      if (au && e.date > au) return false;
    }
    return true;
  });
  return { lignes, sansDateMasquees };
}

/** Combien de critères l'écran a-t-il posés (hors recherche et type, qui ont leur propre place) ? */
export function nbCriteresActifs(c: Criteres): number {
  return [
    periodeActive(c), !!c.equipement, !!c.categorie, !!c.realisePar, !!c.origine,
    c.problemes, c.avecArret, !!c.document, c.sansReprises,
  ].filter(Boolean).length;
}

export const toutEstVide = (c: Criteres): boolean =>
  !c.q.trim() && !c.type && nbCriteresActifs(c) === 0;

/** Les critères en clair, pour l'export et pour le « filtré par » de l'écran. */
export function decrireCriteres(c: Criteres, aujourdhui: string): string[] {
  const out: string[] = [];
  if (c.q.trim()) out.push(`Recherche « ${c.q.trim()} »`);
  if (c.type) out.push(`Type : ${{ preventive: 'préventive', curative: 'curative', non_qualifiee: 'non qualifiée' }[c.type]}`);
  if (periodeActive(c)) {
    const b = bornes(c, aujourdhui);
    out.push(`Période : ${b.du || '…'} → ${b.au || 'aujourd’hui'}`);
  }
  if (c.equipement) out.push(`Équipement : ${c.equipement}`);
  if (c.categorie) out.push(`Catégorie : ${c.categorie}`);
  if (c.realisePar) out.push(`Réalisé par : ${c.realisePar === 'interne' ? 'le laboratoire' : 'un prestataire'}`);
  if (c.origine) out.push(`Origine : ${c.origine === 'plan' ? 'plan préventif' : 'fiche de vie'}`);
  if (c.problemes) out.push('Avec problème (anomalie, non-conformité ou NC)');
  if (c.avecArret) out.push('Appareil arrêté');
  if (c.document) {
    out.push(`Documents : ${c.document === 'aucun' ? 'aucun document joint' : c.document === 'nonprecise' ? 'au moins un de type non précisé' : libelleTypeDocument(c.document)}`);
  }
  if (c.sansReprises) out.push('Sans les reprises');
  return out;
}
